import {DokumentType, DokumentVersjonModell, SigneringStatus, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erArkivdokumentdokumentISak, erProtokolldokumentISak, erProtokolldokumentISakEllerProtokoll, Signatarinformasjon} from "felles-ng2/modell/dokumentModellHjelpere";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {dialogRespons} from "felles-ng2/infrastruktur/material/matDialogHjelpere";
import {SigneringSignerDialogKomponent} from "web-ng2/moduler/signering/modale/signering-signer-dialog/signering-signer-dialog";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {DokumentSendTilSigneringDialogKomponent} from "web-ng2/moduler/dokument/modale/dokument-send-til-signering-dialog/dokument-send-til-signering-dialog";
import {DokumentRedigerTilSigneringDialogKomponent} from "web-ng2/moduler/dokument/modale/dokument-rediger-til-signering-dialog/dokument-rediger-til-signering-dialog";
import {SigneringAvsluttDialogKomponent} from "web-ng2/moduler/signering/modale/signering-avslutt-dialog/signering-avslutt-dialog";
import {Dialog} from "felles-ng2/tjenester/Dialog";
import {MatDialog} from "@angular/material/dialog";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {Injectable} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";

const debug = initDebugger('DokumentSigneringTjeneste');

/** Tjeneste for diverse signeringsrelaterte operasjoner knyttet til dokumenter.
 * Tjenesten kaller dialoger som ligger under web-ng2 katalogen og er derfor ikke lokalisert under felles-ng2. */
@Injectable({providedIn: 'root'})
export class DokumentSigneringTjeneste {

    constructor(
      private oversettelser: Oversettelser,
      private dialog: Dialog,
      private navigasjon: Navigasjon,
      private kontekst:BrukerkontekstTilbyder,
      private matDialog: MatDialog) {
    }

    async signer(dokumentversjon: DokumentVersjonModell, signatarinformasjon: Signatarinformasjon): Promise<boolean> {
        return dialogRespons(SigneringSignerDialogKomponent.vis(this.matDialog, dokumentversjon, signatarinformasjon), false);
    }

    sendTilSigneringMulig(dokumentversjon: DokumentVersjonModell): boolean {
        //øvrige tilstander der dokument ikke kan sendes til signering håndteres av sendTilSignering() som også vil varsle bruker om årsak
        return dokumentversjon.signeringStatus == SigneringStatus.ikkeAktivert &&
          this.harSkrivetilgang(dokumentversjon);
    }

    async sendTilSignering(dokumentversjon: DokumentVersjonModell): Promise<void> {
        //signering kan ikke initieres for arkivdokumentISak - må gjøres fra arkivdokumentet
        if (erArkivdokumentdokumentISak(dokumentversjon)) {
            const bekreftet = await this.dialog.info({
                tittel: this.oversettelser.oversett(i18n('Signer biblioteksdokument')),
                innhold: this.oversettelser.oversett(i18n('{filnavn} er et bibliotekdokument, du må gå til bibliotekinnslaget for å sende dette dokumentet til signering.'), {filnavn: dokumentversjon.filnavn}),
                okKnapp: this.oversettelser.oversett(i18n('Gå til bibliotekinnslaget')),
                avbrytKnapp: this.oversettelser.oversett(i18n('Avbryt'))
            });
            if (bekreftet)
                this.navigasjon.gåTilArkivdokumentKnyttetTilDokumentVersjon(dokumentversjon.id);
            return;
        }
        //signering kan ikke initieres for protokolldokumentISak - må gjøres fra protokoll
        if (erProtokolldokumentISak(dokumentversjon)) {
            const bekreftet = await this.dialog.info({
                tittel: this.oversettelser.oversett(i18n('Signer protokolldokument')),
                innhold: this.oversettelser.oversett(i18n('{filnavn} er et generet protokolldokument, du må gå til protokollen for å sende dette dokumentet til signering.'), {filnavn: dokumentversjon.filnavn}),
                okKnapp: this.oversettelser.oversett(i18n('Gå til møteprotokoll')),
                avbrytKnapp: this.oversettelser.oversett(i18n('Avbryt'))
            });
            if (bekreftet)
                this.navigasjon.gåTilMøteProtokollFraProtokolldokumentISak(dokumentversjon.dokumentId)
            return;
        }
        //meld fra dersom dokument ikke kan signeres
        if (!dokumentversjon.harPdfRepresentasjon) {
            await this.dialog.advarsel({
                tittel: this.oversettelser.oversett(i18n('Signering ikke mulig')),
                innhold: this.oversettelser.oversett(i18n('{filnavn} finnes ikke i PDF-format og kan derfor ikke sendes til signering. Registrer en ny dokumentversjon i PDF-format for å kunne sende til signering.'), {filnavn: dokumentversjon.filnavn}),
            });
            return;
        }
        if (![DokumentType.opplastetSaksdokument, DokumentType.arkivdokumentISak, DokumentType.arkivdokument].includes(dokumentversjon.type)) {
            await this.dialog.advarsel({
                tittel: this.oversettelser.oversett(i18n('Signering ikke mulig')),
                innhold: this.oversettelser.oversett(i18n('Kun opplastede dokumenter kan signeres. Genererte dokumenter som agenda og beslutningsoppfølging kan ikke signeres.')),
            });
            return;
        }
        if (dokumentversjon.pdfFilstoerrelse! > 1024 * 1024 * 10) { //må holdes i synk med no.systemfabrikken.lederplan.domene.dokument.Signering.MAKS_FILSTØRRELSE_SIGNERING
            await this.dialog.advarsel({
                tittel: this.oversettelser.oversett(i18n('Signering ikke mulig')),
                innhold: this.oversettelser.oversett(i18n('{filnavn} er for stor til å kunne sendes til signering. Kun filer mindre enn 10 Mb kan signeres.'), {filnavn: dokumentversjon.filnavn}),
            });
            return;
        }

        //vis send-til-signering-dialogen
        DokumentSendTilSigneringDialogKomponent.vis(this.matDialog, dokumentversjon);
    }

    redigerSignatarerMulig(dokumentversjon: DokumentVersjonModell) {
        if (erProtokolldokumentISakEllerProtokoll(dokumentversjon)) return false;
        return [SigneringStatus.tilSignering, SigneringStatus.delvisSignert].includes(dokumentversjon.signeringStatus) &&
          this.harSkrivetilgang(dokumentversjon);
    }

    avsluttSigneringMulig(dokumentversjon: DokumentVersjonModell): boolean {
        if (erProtokolldokumentISakEllerProtokoll(dokumentversjon)) return false;
        return dokumentversjon.signeringStatus == SigneringStatus.delvisSignert &&
          this.harSkrivetilgang(dokumentversjon);
    }

    kansellerSigneringMulig(dokumentversjon: DokumentVersjonModell): boolean {
        if (erProtokolldokumentISakEllerProtokoll(dokumentversjon)) return false;
        return dokumentversjon.signeringStatus != SigneringStatus.ikkeAktivert &&
          this.harSkrivetilgang(dokumentversjon);
    }

    redigerSignatarer(dokumentversjon: DokumentVersjonModell) {
        DokumentRedigerTilSigneringDialogKomponent.vis(this.matDialog, dokumentversjon);
    }

    avsluttSignering(dokumentversjon: DokumentVersjonModell) {
        if (erProtokolldokumentISakEllerProtokoll(dokumentversjon)) return false;
        SigneringAvsluttDialogKomponent.vis(this.matDialog, dokumentversjon);
    }


    //hjelpemetoder

    private harSkrivetilgang(dokumentversjon: DokumentVersjonModell) {
        return this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, dokumentversjon.gruppeId);
    }

}
